import FileUpload from "@nkr/components/file-upload";
import { useState } from "react";
import { Alert, Button, MenuItem, Modal } from "react-bootstrap";
import { DropEvent, FileRejection } from "react-dropzone";
import * as Papa from 'papaparse';
import { ICompBankInfo } from "@nkr/models/reports/accounts-payable";


type TProps = {
    onBankInfoUploaded: (compBankInfos: ICompBankInfo[]) => void
}

export default function BankInfoModal({ onBankInfoUploaded }: TProps) {
    const [show, setShow] = useState<boolean>(false);
    const [files, setFiles] = useState<File[]>([]);
    const [error, setError] = useState<string>('');

    const cancel = () => {
        setFiles([]);
        setError('');
        setShow(false);
    }

    const handleFileDropped = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
        setFiles(acceptedFiles);
        setError('');
    };

    const handleRemoveFile = () => {
        setFiles([]);
        setError('');
    };

    const fileValid = (data: any[]): boolean => {
        if (!data || data.length === 0) {
            setError('The file is empty.');
            return false;
        }

        for (let i = 0; i < data.length; i++) {
            const row = data[i];
            const rowNum = i + 1;

            if (!('vendorName' in row)) {
                setError(`Row ${rowNum}: Missing required column "vendorName".`);
                return false;
            }
            if (!('bankRoutingNumber' in row)) {
                setError(`Row ${rowNum}: Missing required column "bankRoutingNumber".`);
                return false;
            }
            if (!('bankAccountNumber' in row)) {
                setError(`Row ${rowNum}: Missing required column "bankAccountNumber".`);
                return false;
            }
            if (row.bankRoutingNumber?.length !== 9) {
                setError(`Row ${rowNum}: BankRoutingNumber must be 9 digits (found "${row.bankRoutingNumber}").`);
                return false;
            }
        }

        return true;
    }

    const findDuplicates = (data: ICompBankInfo[]): string[] => {
        const byName = new Map<string, number[]>();

        data.forEach((c, i) => {
            if (!c.vendorName.endsWith(' CCB') && !c.vendorName.endsWith(' OPO')) return;
            const rowNum = i + 2; // +1 for 0-index, +1 for header row
            const rows = byName.get(c.vendorName) ?? [];
            rows.push(rowNum);
            byName.set(c.vendorName, rows);
        });

        return [...byName.entries()]
            .filter(([, rows]) => rows.length > 1)
            .map(([name, rows]) => `${name} (rows ${rows.join(', ')})`);
    }

    const handleSubmit = () => {
        if (files[0]) {
            Papa.parse<ICompBankInfo>(files[0], {
                header: true,
                skipEmptyLines: true,
                transformHeader: (header) => header.charAt(0).toLowerCase() + header.slice(1),
                complete: (results) => {
                    const data = results.data.map(c => ({
                        ...c,
                        vendorName: c.vendorName?.trim(),
                        bankRoutingNumber: c.bankRoutingNumber?.trim(),
                        bankAccountNumber: c.bankAccountNumber?.trim(),
                    }));
                    if (!fileValid(data)) {
                        setFiles([]);
                        return;
                    }
                    const dupes = findDuplicates(data);
                    if (dupes.length > 0) {
                        setFiles([]);
                        setError(`Duplicate entries found for:\n${dupes.join('\n')}`);
                        return;
                    }
                    onBankInfoUploaded(data);
                    cancel();
                },
                error: () => {
                    setFiles([]);
                    setError('Failed to parse the file — ensure it is a valid CSV.');
                }
            });
        }
    };

    return (<>
        <MenuItem onClick={() => setShow(true)}>Validate Bank Info</MenuItem>
        <Modal backdrop='static' show={show} onHide={cancel}>
            <Modal.Header closeButton>
                <Modal.Title>
                    Upload Chase Vendor List
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FileUpload
                    label="Click to Select or Drag and Drop the Chase Vendor List file ..."
                    files={files}
                    accept={['.csv']}
                    singleUploadOnly={true}
                    onFileDropped={handleFileDropped}
                    onRemoveFile={handleRemoveFile} />

                {error && (
                    <Alert bsStyle="danger" style={{marginTop:'20px'}}>
                        {error.split('\n').map((line, i) => <div key={i}>{line}</div>)}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button disabled={!(files && files.length > 0)} bsStyle='primary' onClick={handleSubmit}>Process</Button>
                <Button onClick={cancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    </>);
}
